import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { useLang } from "@/context/LangContext";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";

/* ─────────────────────────────────────────────────────────────────
   Legal pages, Terms, Privacy, Refund.
   /terms, /privacy, /refund
   All three share the same header/footer chrome for a cohesive feel.
   ───────────────────────────────────────────────────────────────── */

const CONTACT_EMAIL = "info@architaly.green";
const COMPANY_NAME = "ESG Report Builder (by Architaly)";
const OPERATOR = "ESG Report Builder Corp";
const LOCATION = "Delaware, United States of America";
const EFFECTIVE_DATE = "22 February 2026";

const H1 = ({ children }) => (
  <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-4 tracking-tight">{children}</h1>
);
const H2 = ({ children }) => (
  <h2 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white mt-10 mb-4 tracking-tight">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[15px] text-slate-300 leading-[1.8] mb-4">{children}</p>
);
const UL = ({ children }) => (
  <ul className="list-disc list-outside pl-5 space-y-2 mb-4 text-[15px] text-slate-300 leading-[1.8]">{children}</ul>
);
const Eyebrow = ({ children }) => (
  <div className="font-['Outfit'] text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-3">{children}</div>
);
const Meta = ({ children, label = "Effective date" }) => (
  <p className="text-xs text-slate-500 mb-8"><strong className="text-slate-400">{label}:</strong> {children}</p>
);

const TERMS = (
  <>
    <Eyebrow>Terms of Service</Eyebrow>
    <H1>Terms of Service</H1>
    <Meta>{EFFECTIVE_DATE}</Meta>
    <P>
      These Terms of Service ("<strong>Terms</strong>") govern your access to and use of the {COMPANY_NAME} website,
      platform, and services (collectively, the "<strong>Service</strong>") operated by {OPERATOR}, a sole trader
      based in {LOCATION} ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>"). By creating an
      account or using the Service, you agree to be bound by these Terms.
    </P>

    <H2>1. The Service</H2>
    <P>
      {COMPANY_NAME} is a software-as-a-service platform that allows businesses to generate AI-assisted ESG
      (Environmental, Social, Governance) reports in English and Arabic, including 60+ page downloadable PDF
      documents aligned with frameworks such as GRI, TCFD, ADX, DFM and the UN Sustainable Development Goals
      (structured to be forward-ready for ISSB and CSRD).
    </P>

    <H2>2. Eligibility</H2>
    <P>
      You must be at least 18 years old and authorised to bind the legal entity (if any) on whose behalf you are
      registering. You are responsible for the accuracy of all information you provide and for all activity under
      your account.
    </P>

    <H2>3. Subscription &amp; Fees</H2>
    <UL>
      <li>Access to the Service requires a paid annual subscription. Current pricing is USD 499 per year, with a 50% launch discount available until 31 October 2026. Prices are displayed at checkout and are exclusive of any applicable local taxes, which are your responsibility in your jurisdiction.</li>
      <li>Payments are securely processed by <strong>Paddle</strong>. Paddle is responsible for the transaction and appears on your card statement.</li>
      <li>Subscriptions renew automatically each year at the then-current price unless cancelled before the renewal date.</li>
      <li>You may cancel your subscription at any time from your account billing page. Cancellation takes effect at the end of the current billing period; no partial refunds are issued for unused time.</li>
    </UL>

    <H2>4. Acceptable Use</H2>
    <P>You agree not to:</P>
    <UL>
      <li>Use the Service for any unlawful, misleading, or fraudulent purpose;</li>
      <li>Upload content that infringes third-party intellectual property or privacy rights;</li>
      <li>Attempt to reverse-engineer, scrape, or resell the Service or any output thereof outside the terms of your own business use;</li>
      <li>Share your account credentials or transfer your subscription to another party.</li>
    </UL>

    <H2>5. Your Content &amp; Generated Reports</H2>
    <P>
      You retain ownership of all business data you upload ("<strong>Customer Data</strong>") and the final
      reports generated for your company. You grant us a limited, non-exclusive licence to process your Customer
      Data strictly to provide the Service. We do not sell your data. Generated report content is provided on a
      non-exclusive basis and is intended for use by your company.
    </P>

    <H2>6. AI-Generated Content Disclaimer</H2>
    <P>
      Report narratives are produced with the assistance of large language models (including Anthropic Claude).
      While the Service aligns output with recognised ESG frameworks, the user is solely responsible for reviewing,
      validating and approving any report prior to public disclosure or regulatory submission. We do not provide
      legal, tax, accounting or audit assurance.
    </P>

    <H2>7. Service Availability</H2>
    <P>
      We strive for high availability but do not guarantee uninterrupted access. Scheduled maintenance, third-party
      outages, or force majeure events may temporarily impact the Service. Credits or refunds for downtime are at
      our sole discretion.
    </P>

    <H2>8. Intellectual Property</H2>
    <P>
      All software, designs, templates, branding, frameworks integration, and visual styles of the Service are the
      intellectual property of {OPERATOR}. Your subscription grants you a right to use the output; it does not
      transfer any ownership in the underlying platform.
    </P>

    <H2>9. Termination</H2>
    <P>
      We may suspend or terminate your account with immediate effect if you materially breach these Terms, abuse the
      Service, or fail to pay. You may delete your account at any time via the Service interface or by contacting
      us at {CONTACT_EMAIL}.
    </P>

    <H2>10. Limitation of Liability</H2>
    <P>
      To the fullest extent permitted by law, our total liability for any claim relating to the Service shall not
      exceed the amount you paid us in the twelve (12) months preceding the event giving rise to the claim. We are
      not liable for indirect, incidental, or consequential damages.
    </P>

    <H2>11. Governing Law</H2>
    <P>
      These Terms are governed by the laws of the United Arab Emirates. Any dispute shall be subject to the
      exclusive jurisdiction of the courts of Delaware, USA, unless mandatory local law applies otherwise.
    </P>

    <H2>12. Changes to These Terms</H2>
    <P>
      We may update these Terms from time to time. If changes are material, we will notify you by email or via
      an in-app notice at least 14 days in advance. Continued use of the Service after the effective date of the
      update constitutes acceptance.
    </P>

    <H2>13. Contact</H2>
    <P>Questions about these Terms? Email us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

const PRIVACY = (
  <>
    <Eyebrow>Privacy Policy</Eyebrow>
    <H1>Privacy Policy</H1>
    <Meta>{EFFECTIVE_DATE}</Meta>
    <P>
      This Privacy Policy explains how {COMPANY_NAME} ("<strong>we</strong>") collects, uses and protects your
      personal data when you use our Service. We are committed to protecting your privacy in compliance with the
      UAE Personal Data Protection Law (PDPL) and, where applicable, the EU General Data Protection Regulation (GDPR).
    </P>

    <H2>1. Data Controller</H2>
    <P>
      The data controller is {OPERATOR}, {LOCATION}. You can contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.
    </P>

    <H2>2. Data We Collect</H2>
    <UL>
      <li><strong>Account data:</strong> name, email, hashed password, preferred language.</li>
      <li><strong>Company data you submit:</strong> company name, sector, country, year, and the operational, environmental, social and governance metrics you enter into the report form.</li>
      <li><strong>Images and files you upload:</strong> company logos, section images.</li>
      <li><strong>Payment data:</strong> handled by Paddle. We receive only the metadata we need to manage your subscription (subscription status, renewal date). We do <em>not</em> see or store full card numbers.</li>
      <li><strong>Technical data:</strong> IP address, browser type, device information, usage analytics, error logs.</li>
      <li><strong>Email correspondence:</strong> any messages you send to our support address.</li>
    </UL>

    <H2>3. How We Use Your Data</H2>
    <UL>
      <li>To provide the Service and generate your ESG reports.</li>
      <li>To authenticate you and secure your account.</li>
      <li>To process subscription payments (via Paddle).</li>
      <li>To send you service notifications (renewal reminders, product updates).</li>
      <li>To improve the Service and debug issues.</li>
      <li>To comply with legal obligations.</li>
    </UL>

    <H2>4. AI Processing</H2>
    <P>
      To generate your reports, we send relevant portions of your company data to Anthropic's Claude API under
      Anthropic's commercial terms, which prohibit training on your data. No personal contact data (e.g. email,
      password) is shared with Anthropic, only the business metrics you submit for reporting purposes.
    </P>

    <H2>5. Data Sharing</H2>
    <P>We share data only with the following categories of processors, each bound by appropriate data-protection terms:</P>
    <UL>
      <li><strong>Paddle</strong>, payment processing.</li>
      <li><strong>Anthropic</strong>, AI narrative generation.</li>
      <li><strong>MongoDB Atlas / hosting provider</strong>, secure storage of your account and report data.</li>
      <li><strong>Microsoft 365 (Office365 SMTP)</strong>, transactional email delivery.</li>
    </UL>
    <P>We do not sell your personal data to anyone. Ever.</P>

    <H2>6. Data Retention</H2>
    <P>
      We retain your account and report data for as long as your subscription is active, plus one (1) year after
      cancellation to support reactivation. After that, data is deleted or anonymised. You can request immediate
      deletion of your account at any time.
    </P>

    <H2>7. Your Rights</H2>
    <P>Under UAE PDPL and GDPR (where applicable), you have the right to:</P>
    <UL>
      <li>Access a copy of your personal data.</li>
      <li>Correct inaccurate data.</li>
      <li>Request deletion ("right to be forgotten").</li>
      <li>Restrict or object to certain processing.</li>
      <li>Data portability, receive your data in a machine-readable format.</li>
      <li>Withdraw consent at any time (for consent-based processing).</li>
    </UL>
    <P>Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a> to exercise any of these rights. We respond within 30 days.</P>

    <H2>8. Security</H2>
    <P>
      We apply industry-standard security measures: encrypted transport (HTTPS/TLS), hashed passwords (bcrypt),
      isolated storage of sensitive documents in GridFS, JWT-based session auth, and role-based access controls.
      No system is 100% secure; if we become aware of a breach affecting your data, we will notify you promptly as
      required by law.
    </P>

    <H2>9. Cookies</H2>
    <P>
      We use only essential cookies (authentication session, language preference). We do not use advertising or
      third-party tracking cookies.
    </P>

    <H2>10. Children</H2>
    <P>The Service is not directed to individuals under 18. We do not knowingly collect data from children.</P>

    <H2>11. International Transfers</H2>
    <P>
      Our hosting and AI providers may process data outside the UAE (typically the EU or the United States).
      Transfers are protected by standard contractual clauses and the respective providers' data-processing addenda.
    </P>

    <H2>12. Contact</H2>
    <P>Privacy questions? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

const REFUND = (
  <>
    <Eyebrow>Refund Policy</Eyebrow>
    <H1>Refund Policy</H1>
    <Meta>{EFFECTIVE_DATE}</Meta>
    <P>
      We stand behind our Service. This Refund Policy explains when you are eligible for a refund of your
      subscription fee.
    </P>

    <H2>1. 14-Day Money-Back Guarantee (First-Time Subscribers Only)</H2>
    <P>
      If you are a <strong>first-time subscriber</strong> and you have <strong>not generated or downloaded any
      ESG report</strong>, you may request a full refund within <strong>fourteen (14) calendar days</strong> of
      your first payment. No questions asked. We will issue the refund within 10 business days via the original
      payment method via our payment processor, Paddle.
    </P>

    <H2>2. When You Are NOT Eligible for a Refund</H2>
    <P>Because our Service delivers its full value as soon as a report is generated, refunds are <strong>not</strong> available in the following cases:</P>
    <UL>
      <li>You have generated one or more ESG reports (even if you have not downloaded the PDF).</li>
      <li>You have downloaded a PDF in any language (English or Arabic).</li>
      <li>You have triggered an Arabic translation on any report.</li>
      <li>The 14-day window from your first payment has expired.</li>
      <li>Your request follows a renewal (subscriptions are billed annually, we send you a reminder 30 / 7 / 1 days before renewal; you may cancel at any time before renewal from your billing page).</li>
      <li>Your account has been suspended or terminated due to a breach of our Terms of Service.</li>
    </UL>

    <H2>3. Cancellation vs. Refund</H2>
    <P>
      You may <strong>cancel</strong> your subscription at any time from the billing page in your account.
      Cancellation stops future renewals but does not automatically issue a refund for the current paid period -
      you retain access until the end of the current billing cycle.
    </P>

    <H2>4. Payment Failures</H2>
    <P>
      If a renewal payment fails, your account is automatically locked. You can restore access by updating your
      payment method via the billing page. No refund or extension is issued for time lost due to a failed payment.
    </P>

    <H2>5. How to Request a Refund</H2>
    <P>
      Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a> from
      the email address registered on your account, with the subject line "<strong>Refund Request</strong>" and your
      order ID (visible in your Paddle receipt). We will reply within 2 business days.
    </P>

    <H2>6. Paddle as Payment Processor</H2>
    <P>
      All payments are securely processed by Paddle. Refunds are issued through Paddle
      and may take up to 10 business days to appear on your statement, depending on your bank.
    </P>

    <H2>7. Chargebacks</H2>
    <P>
      Before initiating a chargeback with your bank, please contact us first, 95% of issues are resolved faster
      by email than by chargeback. Fraudulent or unwarranted chargebacks may result in account termination.
    </P>

    <H2>8. Contact</H2>
    <P>Questions about refunds? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

/* ───────────────── ARABIC (MSA) VERSIONS ───────────────── */

const TERMS_AR = (
  <>
    <Eyebrow>شروط الخدمة</Eyebrow>
    <H1>شروط الخدمة</H1>
    <Meta label="تاريخ السريان">{EFFECTIVE_DATE}</Meta>
    <P>
      تحكم شروط الخدمة هذه ("<strong>الشروط</strong>") وصولك إلى موقع {COMPANY_NAME} ومنصّته وخدماته (يُشار إليها
      مجتمعةً بـ "<strong>الخدمة</strong>") التي يُشغّلها {OPERATOR}، تاجر منفرد مقيم في {LOCATION}
      ("<strong>نحن</strong>"). بإنشائك حساباً أو باستخدامك للخدمة فإنّك تُوافق على الالتزام بهذه الشروط.
    </P>

    <H2>1. الخدمة</H2>
    <P>
      {COMPANY_NAME} منصّة برمجية كخدمة (SaaS) تُتيح للشركات إنشاء تقارير ESG (البيئية والاجتماعية والحوكمة)
      بمساعدة الذكاء الاصطناعي باللغتين العربية والإنجليزية، تتضمّن ملفات PDF قابلة للتنزيل بأكثر من 60 صفحة،
      ومتوافقة مع الأُطر الدولية كـ GRI و TCFD و ADX و DFM وأهداف الأمم المتحدة للتنمية المستدامة (مُهيَّأة للتوافق المستقبلي مع ISSB و CSRD).
    </P>

    <H2>2. الأهليّة</H2>
    <P>
      يجب أن يكون عمرك 18 سنة فأكثر، وأن تكون مُخوَّلاً بالتوقيع بالنيابة عن الكيان القانوني (إن وُجد) الذي تُسجّل
      نيابةً عنه. أنت المسؤول عن صحّة جميع المعلومات التي تُقدّمها وعن كلّ النشاط الذي يجري تحت حسابك.
    </P>

    <H2>3. الاشتراك والرسوم</H2>
    <UL>
      <li>يتطلّب الوصول إلى الخدمة اشتراكاً سنوياً مدفوعاً. السعر الحالي هو 499 دولاراً أمريكياً سنوياً حتى 30 يونيو 2026، و999 دولاراً أمريكياً سنوياً بعد ذلك. الأسعار ظاهرة عند السداد وغير شاملة لأيّ ضرائب محلية مُطبَّقة، وهي مسؤوليتك في بلدك.</li>
      <li>تُعالَج المدفوعات بأمان عبر <strong>Paddle</strong>. وهي الجهة المسؤولة عن المعاملة والتي تظهر في كشف حساب بطاقتك.</li>
      <li>تتجدّد الاشتراكات تلقائياً كلّ سنة بالسعر المعمول به آنذاك، ما لم يتمّ إلغاؤها قبل تاريخ التجديد.</li>
      <li>يمكنك إلغاء اشتراكك في أيّ وقت من صفحة الفوترة في حسابك. يسري الإلغاء في نهاية الفترة الحالية؛ ولا تُمنَح استردادات جزئية عن الوقت غير المستخدم.</li>
    </UL>

    <H2>4. الاستخدام المقبول</H2>
    <P>توافق على عدم:</P>
    <UL>
      <li>استخدام الخدمة لأيّ غرض غير مشروع أو مُضلِّل أو احتيالي؛</li>
      <li>رفع أيّ محتوى يُخالف حقوق الملكية الفكرية أو الخصوصية لأطراف ثالثة؛</li>
      <li>محاولة الهندسة العكسية للخدمة أو استخراج بياناتها أو إعادة بيع مُخرجاتها خارج نطاق الاستخدام التجاري الخاصّ بشركتك؛</li>
      <li>مشاركة بيانات حسابك أو نقل اشتراكك إلى طرف آخر.</li>
    </UL>

    <H2>5. محتواك والتقارير المُنشأة</H2>
    <P>
      تحتفظ بملكية جميع بيانات الأعمال التي ترفعها ("<strong>بيانات العميل</strong>") والتقارير النهائية
      المُنشأة لشركتك. تمنحنا ترخيصاً محدوداً غير حصري لمعالجة بيانات العميل حصراً لتقديم الخدمة. لا نبيع بياناتك.
      يُوفَّر محتوى التقارير المُنشأة على أساس غير حصري، ومُعدّ للاستخدام من قِبل شركتك.
    </P>

    <H2>6. إخلاء المسؤولية عن محتوى الذكاء الاصطناعي</H2>
    <P>
      تُنتَج السرديات في التقارير بمساعدة نماذج لغوية كبيرة (بما فيها Anthropic Claude). وفي حين تُوائم الخدمة
      مُخرجاتها مع أُطر ESG المعتمَدة، يتحمّل المستخدم وحده مسؤولية مراجعة التقرير والتحقّق منه والموافقة عليه قبل
      أيّ إفصاح علنيّ أو إيداع تنظيمي. لا نُقدّم أيّ خدمات قانونية أو ضريبية أو محاسبية أو تدقيقية.
    </P>

    <H2>7. توافر الخدمة</H2>
    <P>
      نسعى لضمان توافر عالٍ دون أن نضمن استمرارية الوصول بلا انقطاع. قد تُؤثّر الصيانة المُجدوَلة، أو انقطاعات
      مزوّدي الخدمة الخارجيين، أو أحداث القوّة القاهرة، على الخدمة بصورة مؤقّتة. تُمنَح أيّ تعويضات عن فترات
      التوقّف وفقاً لتقديرنا المطلق.
    </P>

    <H2>8. الملكية الفكرية</H2>
    <P>
      جميع البرمجيات والتصاميم والقوالب وتكاملات الأُطر والعلامة التجارية والأساليب البصرية للخدمة هي ملكية فكرية
      لـ {OPERATOR}. يمنحك اشتراكك حقّ استخدام المُخرجات؛ ولا يُنقَل إليك أيّ مُلكيّة في المنصّة الأساسية.
    </P>

    <H2>9. الإنهاء</H2>
    <P>
      يحقّ لنا تعليق حسابك أو إنهاؤه فوراً في حال إخلالك الجوهري بهذه الشروط، أو إساءة استخدام الخدمة، أو
      التخلّف عن السداد. ويحقّ لك حذف حسابك في أيّ وقت عبر واجهة الخدمة أو بمراسلتنا على {CONTACT_EMAIL}.
    </P>

    <H2>10. حدود المسؤولية</H2>
    <P>
      إلى أقصى حدّ يُجيزه القانون، لا تتجاوز مسؤوليتنا الإجمالية عن أيّ مُطالبة تتعلّق بالخدمة المبلغَ الذي دفعتَه
      لنا في الاثني عشر (12) شهراً السابقة للحدث المُنشِئ للمطالبة. ولسنا مسؤولين عن أيّ أضرار غير مباشرة أو عَرَضية
      أو تبعية.
    </P>

    <H2>11. القانون الحاكم</H2>
    <P>
      تخضع هذه الشروط لقوانين ولاية ديلاوير بالولايات المتحدة الأمريكية. وأيّ نزاع ينشأ عنها يخضع للاختصاص الحصري لمحاكم ولاية ديلاوير،
      ما لم يُوجِب القانون المحلّي خلاف ذلك.
    </P>

    <H2>12. تعديلات الشروط</H2>
    <P>
      قد نُحدّث هذه الشروط من حين لآخر. إذا كانت التعديلات جوهرية، سنُعلمك عبر البريد الإلكتروني أو بإشعار داخل
      التطبيق قبل 14 يوماً على الأقلّ. يُعدّ استمرارك في استخدام الخدمة بعد تاريخ السريان قبولاً للتعديل.
    </P>

    <H2>13. التواصل</H2>
    <P>لأيّ استفسار بشأن هذه الشروط، راسلنا على <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

const PRIVACY_AR = (
  <>
    <Eyebrow>سياسة الخصوصية</Eyebrow>
    <H1>سياسة الخصوصية</H1>
    <Meta label="تاريخ السريان">{EFFECTIVE_DATE}</Meta>
    <P>
      تُوضّح سياسة الخصوصية هذه كيفية قيامنا, {COMPANY_NAME} ("<strong>نحن</strong>"), بجمع بياناتك الشخصية
      واستخدامها وحمايتها عند استخدامك للخدمة. نلتزم بحماية خصوصيتك وفقاً لقانون حماية البيانات الشخصية في دولة
      الإمارات (PDPL)، ووفقاً لاتحاد اللائحة العامة لحماية البيانات في الاتحاد الأوروبي (GDPR) حيث تنطبق.
    </P>

    <H2>1. مُراقِب البيانات</H2>
    <P>
      مُراقِب البيانات هو {OPERATOR}، {LOCATION}. يمكنك التواصل معنا على
      <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline"> {CONTACT_EMAIL}</a>.
    </P>

    <H2>2. البيانات التي نجمعها</H2>
    <UL>
      <li><strong>بيانات الحساب:</strong> الاسم، والبريد الإلكتروني، وكلمة المرور المُشفَّرة، واللغة المُفضَّلة.</li>
      <li><strong>بيانات الشركة التي تُرسلها:</strong> اسم الشركة، والقطاع، والدولة، والسنة، ومؤشّرات الأداء التشغيلية والبيئية والاجتماعية والحوكمية التي تُدخلها في نموذج التقرير.</li>
      <li><strong>الصور والملفات التي ترفعها:</strong> شعارات الشركة، وصور الأقسام.</li>
      <li><strong>بيانات الدفع:</strong> تُعالَج بواسطة Paddle. نحصل فقط على البيانات التعريفية اللازمة لإدارة اشتراكك (حالة الاشتراك، تاريخ التجديد). <em>لا نرى ولا نُخزّن</em> أرقام البطاقات الكاملة.</li>
      <li><strong>البيانات التقنية:</strong> عنوان IP، ونوع المتصفّح، ومعلومات الجهاز، وتحليلات الاستخدام، وسجلّات الأخطاء.</li>
      <li><strong>المراسلات البريدية:</strong> أيّ رسائل تُرسلها إلى بريد الدعم لدينا.</li>
    </UL>

    <H2>3. كيف نستخدم بياناتك</H2>
    <UL>
      <li>تقديم الخدمة وإنشاء تقارير ESG الخاصّة بك.</li>
      <li>مصادقة حسابك وتأمينه.</li>
      <li>معالجة مدفوعات الاشتراك (عبر Paddle).</li>
      <li>إرسال إشعارات الخدمة (تذكيرات التجديد، وتحديثات المنتج).</li>
      <li>تحسين الخدمة وتصحيح الأخطاء.</li>
      <li>الامتثال للالتزامات القانونية.</li>
    </UL>

    <H2>4. المعالجة بالذكاء الاصطناعي</H2>
    <P>
      لإنشاء تقاريرك، نُرسل الأجزاء ذات الصلة من بيانات شركتك إلى واجهة Claude من Anthropic وفقاً للشروط التجارية
      التي تحظر تدريب النماذج على بياناتك. لا تتمّ مشاركة أيّ بيانات اتّصال شخصية (كالبريد الإلكتروني أو كلمة
      المرور) مع Anthropic, بل مؤشّرات الأعمال التي تُقدّمها لأغراض إعداد التقرير فقط.
    </P>

    <H2>5. مشاركة البيانات</H2>
    <P>نشارك البيانات فقط مع الفئات التالية من مُعالجات الطرف الثالث، كلٌّ منها مُلزَم بشروط ملائمة لحماية البيانات:</P>
    <UL>
      <li><strong>Paddle</strong>, معالجة المدفوعات.</li>
      <li><strong>Anthropic</strong>, توليد السرديات بالذكاء الاصطناعي.</li>
      <li><strong>MongoDB Atlas / مزوّد الاستضافة</strong>, تخزين آمن لبيانات حسابك وتقاريرك.</li>
      <li><strong>Microsoft 365 (Office365 SMTP)</strong>, إرسال بريد المعاملات.</li>
    </UL>
    <P>لا نبيع بياناتك الشخصية لأيّ طرف. أبداً.</P>

    <H2>6. الاحتفاظ بالبيانات</H2>
    <P>
      نحتفظ ببيانات حسابك وتقاريرك طوال فترة اشتراكك، إضافةً إلى سنة واحدة (1) بعد الإلغاء لدعم إعادة التفعيل. بعد
      ذلك، تُحذَف البيانات أو يُزال التعريف منها. ويمكنك طلب الحذف الفوري لحسابك في أيّ وقت.
    </P>

    <H2>7. حقوقك</H2>
    <P>وفقاً لـ PDPL الإماراتي و GDPR (حيث ينطبق)، لك الحقّ في:</P>
    <UL>
      <li>الوصول إلى نسخة من بياناتك الشخصية.</li>
      <li>تصحيح البيانات غير الدقيقة.</li>
      <li>طلب الحذف ("الحقّ في النسيان").</li>
      <li>تقييد معالجة معيّنة أو الاعتراض عليها.</li>
      <li>نقل البيانات, استلامها بصيغة قابلة للقراءة آلياً.</li>
      <li>سحب الموافقة في أيّ وقت (للمعالجة القائمة على الموافقة).</li>
    </UL>
    <P>للتواصل بشأن ممارسة أيّ من هذه الحقوق، راسلنا على <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>. نردّ خلال 30 يوماً.</P>

    <H2>8. الأمان</H2>
    <P>
      نُطبّق تدابير أمنية وفق معايير القطاع: نقل مُشفَّر (HTTPS/TLS)، وكلمات مرور مُشفَّرة (bcrypt)، وتخزين معزول
      للمستندات الحسّاسة في GridFS، ومصادقة جلسات بـ JWT، وضوابط وصول قائمة على الأدوار. لا يوجد نظام آمن بنسبة
      100٪؛ وإذا علمنا بأيّ اختراق يُؤثّر على بياناتك، فسنُبلغك فوراً وفقاً لما يُوجبه القانون.
    </P>

    <H2>9. ملفّات تعريف الارتباط (Cookies)</H2>
    <P>
      نستخدم فقط ملفّات تعريف ارتباط أساسية (جلسة المصادقة، تفضيل اللغة). لا نستخدم ملفّات تعريف ارتباط للإعلانات
      أو للتتبّع من طرف ثالث.
    </P>

    <H2>10. الأطفال</H2>
    <P>الخدمة غير موجَّهة للأفراد دون 18 سنة. ولا نجمع عن علم بيانات من الأطفال.</P>

    <H2>11. النقل الدولي</H2>
    <P>
      قد يُعالِج مزوّدو الاستضافة والذكاء الاصطناعي لدينا البيانات خارج دولة الإمارات (في الاتحاد الأوروبي أو
      الولايات المتحدة عادةً). تُحمى عمليات النقل بالبنود التعاقدية المعيارية وملاحق معالجة البيانات لدى المزوّد
      المعنيّ.
    </P>

    <H2>12. التواصل</H2>
    <P>لأيّ استفسار بشأن الخصوصية، راسلنا على <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

const REFUND_AR = (
  <>
    <Eyebrow>سياسة الاسترداد</Eyebrow>
    <H1>سياسة الاسترداد</H1>
    <Meta label="تاريخ السريان">{EFFECTIVE_DATE}</Meta>
    <P>
      نحن نقف خلف خدمتنا. تُوضّح سياسة الاسترداد هذه الحالات التي تستحقّ فيها استرداد رسوم اشتراكك.
    </P>

    <H2>1. ضمان استرداد الأموال لمدّة 14 يوماً (للمشتركين لأوّل مرّة فقط)</H2>
    <P>
      إذا كنتَ <strong>مشتركاً لأوّل مرّة</strong> ولم <strong>تقُم بإنشاء أيّ تقرير ESG أو تنزيله</strong>،
      فيحقّ لك طلب استرداد كامل خلال <strong>أربعة عشر (14) يوماً تقويمياً</strong> من تاريخ أوّل دفعة.
      دون الحاجة إلى تبرير. نُصدر الاسترداد خلال 10 أيّام عمل عبر طريقة الدفع الأصلية من خلال معالج المدفوعات Paddle.
    </P>

    <H2>2. متى لا يحقّ لك الاسترداد</H2>
    <P>نظراً لأنّ خدمتنا تُقدّم قيمتها الكاملة فور إنشاء التقرير، لا تتوفّر استردادات في الحالات التالية:</P>
    <UL>
      <li>قمتَ بإنشاء تقرير ESG واحد أو أكثر (حتى لو لم تُنزِّل ملف PDF).</li>
      <li>قمتَ بتنزيل ملف PDF بأيّ لغة (بالعربية أو بالإنجليزية).</li>
      <li>قمتَ بتفعيل ترجمة عربية لأيّ تقرير.</li>
      <li>انقضت فترة الـ 14 يوماً منذ أوّل دفعة.</li>
      <li>طلبك يلي تجديداً (الاشتراكات سنوية, نُرسل تذكيراً قبل التجديد بـ 30 و 7 و 1 يوم؛ ويمكنك الإلغاء قبل التجديد من صفحة الفوترة).</li>
      <li>تمّ تعليق حسابك أو إنهاؤه بسبب انتهاك شروط الخدمة.</li>
    </UL>

    <H2>3. الإلغاء مقابل الاسترداد</H2>
    <P>
      يمكنك <strong>إلغاء</strong> اشتراكك في أيّ وقت من صفحة الفوترة في حسابك. يوقف الإلغاء التجديدات المستقبلية
      لكنّه لا يُصدر استرداداً تلقائياً للفترة المدفوعة الحالية, تحتفظ بالوصول حتى نهاية الدورة الحالية.
    </P>

    <H2>4. فشل الدفع</H2>
    <P>
      إذا فشلت دفعة التجديد، يُقفَل حسابك تلقائياً. يمكنك استعادة الوصول بتحديث طريقة الدفع من صفحة الفوترة. لا
      يُصدَر أيّ استرداد أو تمديد مقابل الوقت المفقود بسبب فشل الدفع.
    </P>

    <H2>5. كيفية طلب الاسترداد</H2>
    <P>
      راسل <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>
      {" "}من عنوان البريد المُسجَّل في حسابك بعنوان الرسالة "<strong>طلب استرداد</strong>" مع رقم طلبك (المرئي في
      إيصال Paddle). نردّ خلال يومَي عمل.
    </P>

    <H2>6. Paddle بصفتها معالج المدفوعات</H2>
    <P>
      تُعالَج جميع المدفوعات بأمان عبر Paddle. تُصدَر الاستردادات من خلال Paddle وقد
      تستغرق حتى 10 أيّام عمل لتظهر في كشف حسابك، وفقاً لبنكك.
    </P>

    <H2>7. الاعتراض على الدفعات (Chargebacks)</H2>
    <P>
      قبل تقديم اعتراض عبر البنك، يُرجى التواصل معنا أوّلاً, 95٪ من الحالات تُحلّ عبر البريد الإلكتروني أسرع من
      الاعتراض البنكي. قد تُؤدّي الاعتراضات الاحتيالية أو غير المبرَّرة إلى إنهاء الحساب.
    </P>

    <H2>8. التواصل</H2>
    <P>لأيّ استفسار بشأن الاسترداد، راسلنا على <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a>.</P>
  </>
);

const KIND_MAP = {
  terms: { en: TERMS, ar: TERMS_AR },
  privacy: { en: PRIVACY, ar: PRIVACY_AR },
  refund: { en: REFUND, ar: REFUND_AR },
};

export default function LegalPage({ kind }) {
  const navigate = useNavigate();
  const params = useParams();
  const { lang, isRtl } = useLang();
  const key = kind || params.kind || "terms";
  const entry = KIND_MAP[key] || KIND_MAP.terms;
  const body = lang === "ar" ? entry.ar : entry.en;
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const homeLabel = lang === "ar" ? "الرئيسية" : "Home";
  const termsLabel = lang === "ar" ? "الشروط" : "Terms";
  const privacyLabel = lang === "ar" ? "الخصوصية" : "Privacy";
  const refundLabel = lang === "ar" ? "الاسترداد" : "Refunds";
  const operatorLabel = lang === "ar" ? "المُشغِّل" : "Operator";
  const contactLabel = lang === "ar" ? "التواصل" : "Contact";
  const morLabel = lang === "ar" ? "معالج المدفوعات" : "Payment Processor";
  const copyrightLabel = lang === "ar"
    ? "© 2026 ESG Report Builder Corp · جميع الحقوق محفوظة."
    : "© 2026 ESG Report Builder Corp · All rights reserved.";

  return (
    <div className="min-h-screen bg-[#070709] text-slate-100">
      {/* NAV */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-[#070709]/90 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex-shrink-0"><Logo size="md" /></div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <LangSwitch />
            <button onClick={() => navigate("/")} data-testid="legal-back-home"
              className="font-['Outfit'] text-xs sm:text-sm font-semibold text-slate-300 hover:text-white transition-colors inline-flex items-center gap-1.5">
              <BackIcon size={16} /> {homeLabel}
            </button>
          </div>
        </div>
      </nav>

      {/* CONTENT */}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-20" data-testid={`legal-${key}`}>
        {body}

        <div className="mt-16 pt-8 border-t border-white/[0.06] text-xs text-slate-500 space-y-1">
          <p><strong className="text-slate-400">{operatorLabel}:</strong> {OPERATOR} &bull; {LOCATION}</p>
          <p><strong className="text-slate-400">{contactLabel}:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#01AF9B] hover:underline">{CONTACT_EMAIL}</a></p>
          <p><strong className="text-slate-400">{morLabel}:</strong> Paddle</p>
        </div>
      </article>

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06] py-8 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/terms")} className="hover:text-[#01AF9B] transition-colors">{termsLabel}</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#01AF9B] transition-colors">{privacyLabel}</button>
            <button onClick={() => navigate("/refund")} className="hover:text-[#01AF9B] transition-colors">{refundLabel}</button>
          </div>
          <p>{copyrightLabel}</p>
        </div>
      </footer>
    </div>
  );
}
